// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { ILinkGroup } from '@/interfaces';
import {
    defaultGetOptions,
    fetchGetWithRetryAsync,
    getErrorMessage,
    isAbortError,
    logErrorToConsole,
} from '@/utils/apiUtils';
import { Type } from '@/utils/result';

/**
 * A cancelled request is not a failure, so the AbortError is handed back
 * as-is and callers can tell it apart with isAbortError.
 */
function toFailure(error: unknown): Error {
    if (isAbortError(error)) {
        return error as Error;
    }

    logErrorToConsole(error);
    return new Error(getErrorMessage(error));
}

export async function getCountriesAsync(
    signal?: AbortSignal
): Promise<Type<Array<any>>> {
    try {
        const response = await fetchGetWithRetryAsync(
            '/i18n/countries.json',
            defaultGetOptions(signal)
        );
        if (response.ok) {
            return await response.json();
        } else {
            throw new Error(response.statusText);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export async function getDefaultCountryAsync(
    signal?: AbortSignal
): Promise<Type<string>> {
    try {
        const response = await fetchGetWithRetryAsync(
            '/i18n/currentlocation',
            defaultGetOptions(signal)
        );
        if (response.ok) {
            return await response.text();
        } else {
            throw new Error(response.statusText);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export async function getLinkGroupsAsync(
    signal?: AbortSignal
): Promise<Type<Array<ILinkGroup>>> {
    try {
        const response = await fetchGetWithRetryAsync(
            '/i18n/linkGroups.json',
            defaultGetOptions(signal)
        );
        if (response.ok) {
            return await response.json();
        } else {
            throw new Error(response.statusText);
        }
    } catch (error) {
        return toFailure(error);
    }
}

export default {
    getLinkGroupsAsync,
};
